// The mssql-labelled face of the engine-neutral introspection query layer
// (SLICES.md § S7 items 2 + 4 + 7). Every paged mssql fetcher —
// `mssqlColumnIntrospection`, `mssqlSchemaIntrospection`,
// `mssqlIndexIntrospection`, `mssqlTableCache`'s table list — routes through
// here, which fixes `engine: 'mssql'` and T-SQL's `OFFSET/FETCH` page spelling
// and otherwise defers to `introspectionQuery.ts`. S7 item 8 rolled that
// module out to pg / mysql / oracle / sqlite, which call it directly.

import type {
  IntrospectionPageResult,
  IntrospectionRow,
  PagedIntrospectionOutcome,
} from './introspectionPaging';
import {
  fetchIntrospectionPage,
  fetchPagedArtefact,
  type PagedFetchOptions,
} from './introspectionQuery';
import type { TruncationArtefact } from './introspectionTruncation';

export type { PagedFetchOptions };

/** Issue ONE page query through the connection's introspection lane. */
export async function fetchMssqlIntrospectionPage(args: {
  readonly connectionId: string;
  readonly database: string;
  /** Short slug for the executionId (`cols`, `fks`, `indexes`, …). */
  readonly label: string;
  readonly sql: string;
}): Promise<IntrospectionPageResult> {
  return fetchIntrospectionPage({ engine: 'mssql', ...args });
}

/**
 * Page one mssql introspection artefact end to end: build each page's SQL from
 * `sqlForPage(offsetFetchClause)`, aggregate + sink it, yield between pages,
 * and report the truncation (with the artefact name) if the TOTAL row budget
 * is spent before the server runs out of rows.
 */
export async function fetchMssqlPagedArtefact<TItem>(args: {
  readonly connectionId: string;
  readonly database: string;
  readonly label: string;
  readonly artefact: TruncationArtefact;
  readonly sqlForPage: (offsetFetch: string) => string;
  readonly aggregate: (rows: readonly IntrospectionRow[]) => TItem[];
  readonly sink: (items: readonly TItem[]) => void | Promise<void>;
  readonly totalBudget: number;
  readonly groupKeyOf?: (row: IntrospectionRow) => string;
  readonly shouldAbort?: () => boolean;
}): Promise<PagedIntrospectionOutcome> {
  return fetchPagedArtefact<TItem>({ engine: 'mssql', dialect: 'offset-fetch', ...args });
}
